import React, { useEffect, useState, useRef } from 'react';
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  CircularProgress,
  Container,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditIcon from '@mui/icons-material/Edit';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import { useAuth } from '../AuthContext';
import { updateCatch, deleteCatch, updateCatchImage, fetchUserRods, fetchUserBaits } from '../api';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { API_URL } from '../config';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export default function CatchGallery({ userId }) {
  const { token, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [catches, setCatches] = useState([]);
  const [error, setError] = useState('');

  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedCatch, setSelectedCatch] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  const [localLikes, setLocalLikes] = useState({});

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editValues, setEditValues] = useState({
    species: '',
    size: '',
    bait: '',
    rod: '',
    location: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const editFileInputRef = useRef(null);

  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editGeo, setEditGeo] = useState(null); // { latitude, longitude }
  const [locationResolving, setLocationResolving] = useState(false);

  const [userRods, setUserRods] = useState([]);
  const [userBaits, setUserBaits] = useState([]);

  const isOwnProfile = user && String(user.id) === String(userId);

  useEffect(() => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_URL}/catches/users/${userId}`, { headers })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        }
        if (res.status === 403) {
          setError('This user does not share their catches.');
          return [];
        }
        throw new Error('Failed to load catches');
      })
      .then((data) => {
        setCatches(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((e) => {
        if (!error) {
          setError(e.message || 'Failed to load catches');
        }
        setLoading(false);
      });
  }, [userId, token]);

  const resolveImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const normalized = path.startsWith('/') ? path.slice(1) : path;
    return `${API_URL}/${normalized}`;
  };

  const handleLike = async (catchId) => {
    if (!token) {
      setError('You need to be logged in to like catches');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/catches/${catchId}/like`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Like failed');
      const data = await res.json();
      setCatches((prev) =>
        prev.map((c) =>
          c.id === catchId ? { ...c, like_count: data.like_count } : c
        )
      );
      setLocalLikes((prev) => ({ ...prev, [catchId]: !!data.liked }));
    } catch (e) {
      setError(e.message || 'Failed to like catch');
    }
  };

  const openComments = async (catchItem) => {
    setSelectedCatch(catchItem);
    setComments([]);
    setNewComment('');
    setCommentsDialogOpen(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/catches/${catchItem.id}/comments`, { headers });
      if (!res.ok) throw new Error('Failed to load comments');
      const data = await res.json();
      setComments(data);
    } catch (e) {
      setError(e.message || 'Failed to load comments');
    }
  };

  const closeComments = () => {
    setCommentsDialogOpen(false);
    setSelectedCatch(null);
  };

  const handleAddComment = async () => {
    if (!token || !selectedCatch || !newComment.trim()) {
      if (!token) setError('You need to be logged in to comment');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/catches/${selectedCatch.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: newComment.trim() }),
      });
      if (!res.ok) throw new Error('Failed to add comment');
      const created = await res.json();
      setComments((prev) => [...prev, created]);
      setNewComment('');
      setCatches((prev) =>
        prev.map((c) =>
          c.id === selectedCatch.id
            ? { ...c, comment_count: (c.comment_count || 0) + 1 }
            : c
        )
      );
    } catch (e) {
      setError(e.message || 'Failed to add comment');
    }
  };

  const openEdit = async (catchItem) => {
    if (!isOwnProfile) return;
    setSelectedCatch(catchItem);
    setEditValues({
      species: catchItem.species || '',
      size: String(catchItem.size ?? ''),
      bait: catchItem.bait || '',
      rod: catchItem.rod || '',
      location: catchItem.location || '',
    });
    if (typeof catchItem.latitude === 'number' && typeof catchItem.longitude === 'number') {
      setEditGeo({ latitude: catchItem.latitude, longitude: catchItem.longitude });
    } else {
      setEditGeo(null);
    }
    setEditImageFile(null);
    setEditImagePreview(catchItem.image_path ? resolveImageUrl(catchItem.image_path) : null);
    setEditDialogOpen(true);
    // Fetch user's rods and baits for dropdowns
    if (token) {
      try {
        const [rods, baits] = await Promise.all([
          fetchUserRods(token),
          fetchUserBaits(token),
        ]);
        setUserRods(Array.isArray(rods) ? rods : []);
        setUserBaits(Array.isArray(baits) ? baits : []);
      } catch {
        // Ignore errors, dropdowns will just be empty
      }
    }
  };

  const handleEditImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setEditImageFile(null);
      setEditImagePreview(selectedCatch?.image_path ? resolveImageUrl(selectedCatch.image_path) : null);
      return;
    }
    setEditImageFile(file);
    const url = URL.createObjectURL(file);
    setEditImagePreview(url);
  };

  const openEditImagePicker = () => {
    if (editFileInputRef.current) {
      editFileInputRef.current.click();
    }
  };

  const handleSaveEdit = async () => {
    if (!token || !selectedCatch) {
      setError('You need to be logged in to edit catches');
      return;
    }
    try {
      const payload = {
        species: editValues.species.trim() || undefined,
        size: editValues.size ? Number(editValues.size) : undefined,
        bait: editValues.bait.trim() || undefined,
        rod: editValues.rod.trim() || undefined,
        location: editValues.location.trim() || undefined,
        latitude:
          editGeo && typeof editGeo.latitude === 'number'
            ? editGeo.latitude
            : undefined,
        longitude:
          editGeo && typeof editGeo.longitude === 'number'
            ? editGeo.longitude
            : undefined,
      };
      const updatedMeta = await updateCatch(token, selectedCatch.id, payload);
      let updatedCatch = updatedMeta;
      if (editImageFile) {
        const updatedWithImage = await updateCatchImage(token, selectedCatch.id, editImageFile);
        updatedCatch = updatedWithImage;
      }
      setCatches((prev) =>
        prev.map((c) =>
          c.id === selectedCatch.id
            ? {
                ...c,
                ...updatedCatch,
                like_count: c.like_count,
                comment_count: c.comment_count,
              }
            : c
        )
      );
      setEditDialogOpen(false);
      setSelectedCatch(null);
      setEditImageFile(null);
      setEditImagePreview(null);
    } catch (e) {
      setError(e.message || 'Failed to update catch');
    }
  };

  const handleDelete = async () => {
    if (!token || !selectedCatch) {
      setError('You need to be logged in to delete catches');
      return;
    }
    try {
      await deleteCatch(token, selectedCatch.id);
      setCatches((prev) => prev.filter((c) => c.id !== selectedCatch.id));
      setEditDialogOpen(false);
      setSelectedCatch(null);
      setEditImageFile(null);
      setEditImagePreview(null);
    } catch (e) {
      setError(e.message || 'Failed to delete catch');
    }
  };

  const reverseGeocode = async (latitude, longitude) => {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=jsonv2&addressdetails=1`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const data = await res.json();
      const addr = data?.address || {};
      const primary = addr.city || addr.town || addr.village || addr.hamlet || addr.suburb || data?.name;
      const region = addr.state || addr.region || addr.county;
      const country = addr.country;
      const parts = [primary, region, country].filter(Boolean);
      return parts.length ? parts.join(', ') : null;
    } catch {
      return null;
    }
  };

  const setLocationFromCoords = async (latitude, longitude) => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
    setEditGeo({ latitude, longitude });
    setLocationResolving(true);
    const human = await reverseGeocode(latitude, longitude);
    setEditValues((prev) => ({
      ...prev,
      location: human || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
    }));
    setLocationResolving(false);
  };

  function LocationMarker({ position, onMove }) {
    const [pos, setPos] = useState(position);
    useMapEvents({
      click(e) {
        setPos(e.latlng);
        if (onMove) onMove(e.latlng);
      },
    });

    useEffect(() => {
      if (position) {
        setPos(position);
      }
    }, [position]);

    if (!pos) return null;
    return (
      <Marker
        position={pos}
        draggable
        eventHandlers={{
          dragend: (e) => {
            const latlng = e.target.getLatLng();
            setPos(latlng);
            if (onMove) onMove(latlng);
          },
        }}
      />
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        My Catches
      </Typography>
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      {loading ? (
        <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />
      ) : (
        <Grid container spacing={2}>
          {catches.map((catchItem) => (
            <Grid item xs={12} sm={6} md={4} key={catchItem.id}>
              <Card
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  position: 'relative',
                }}
              >
                <CardMedia
                  component="img"
                  height="160"
                  image={resolveImageUrl(catchItem.image_path)}
                  alt={catchItem.species}
                />
                {isOwnProfile && (
                  <IconButton
                    size="small"
                    onClick={() => openEdit(catchItem)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      color: '#D4AF37',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.85)',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                <CardContent sx={{ pt: 1.5, pb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleLike(catchItem.id)}
                      sx={{
                        mr: 0.5,
                        color: localLikes[catchItem.id] ? 'error.main' : 'inherit',
                      }}
                    >
                      <FavoriteBorderIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openComments(catchItem)}>
                      <ChatBubbleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(catchItem.like_count || 0) === 1
                      ? '1 like'
                      : `${catchItem.like_count || 0} likes`}
                  </Typography>
                  {catchItem.comment_count > 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openComments(catchItem)}
                    >
                      {catchItem.comment_count === 1
                        ? 'View 1 comment'
                        : `View all ${catchItem.comment_count} comments`}
                    </Typography>
                  )}
                  <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                    {catchItem.species} ({catchItem.size} cm)
                  </Typography>
                  {(catchItem.rod || catchItem.bait) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {catchItem.rod}
                      {catchItem.rod && catchItem.bait && ' · '}
                      {catchItem.bait}
                    </Typography>
                  )}
                  {catchItem.location && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {catchItem.location}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {(() => {
                      if (!catchItem.caught_at) return '';
                      const d = new Date(catchItem.caught_at);
                      if (isNaN(d.getTime())) return catchItem.caught_at;
                      const date = d.toLocaleDateString();
                      const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                      return `${date} ${time}`;
                    })()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Hidden file input for editing image */}
      <input
        type="file"
        accept="image/*"
        ref={editFileInputRef}
        style={{ display: 'none' }}
        onChange={handleEditImageChange}
      />

      {/* Comments dialog */}
      <Dialog open={commentsDialogOpen} onClose={closeComments} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 0.5 }}>
          {selectedCatch
            ? `${selectedCatch.species} (${selectedCatch.size} cm)`
            : 'Comments'}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: 360, overflowY: 'auto', pt: 0 }}>
          {comments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Be the first to comment.
            </Typography>
          ) : (
            comments.map((c) => (
              <Box key={c.id} sx={{ display: 'flex', mb: 1.5 }}>
                <Box sx={{ mr: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      bgcolor: 'grey.700',
                    }}
                  >
                    {c.author_profile_picture && (
                      <img
                        src={resolveImageUrl(c.author_profile_picture)}
                        alt={c.author_nickname || 'User'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {c.author_nickname || 'Anonymous'}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {c.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.created_at}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions
          sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2, pt: 0 }}
        >
          <TextField
            placeholder="Add a comment..."
            fullWidth
            size="small"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, width: '100%' }}
          >
            <Button onClick={closeComments} sx={{ mr: 1 }}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleAddComment}
              disabled={!newComment.trim()}
            >
              Post
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Edit catch dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedCatch(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Edit catch</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {selectedCatch && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Tap the photo to change it
              </Typography>
              <Box
                component="img"
                src={editImagePreview || resolveImageUrl(selectedCatch.image_path)}
                alt={selectedCatch.species}
                sx={{
                  width: '100%',
                  maxHeight: 260,
                  objectFit: 'cover',
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '1px solid rgba(212, 175, 55, 0.4)',
                }}
                onClick={openEditImagePicker}
              />
            </Box>
          )}
          <TextField
            label="Species"
            fullWidth
            value={editValues.species}
            onChange={(e) =>
              setEditValues((prev) => ({ ...prev, species: e.target.value }))
            }
          />
          <TextField
            label="Size (cm)"
            type="number"
            fullWidth
            value={editValues.size}
            onChange={(e) =>
              setEditValues((prev) => ({ ...prev, size: e.target.value }))
            }
          />
          <Autocomplete
            freeSolo
            options={userBaits.map((b) => b.name)}
            value={editValues.bait}
            onInputChange={(e, value) =>
              setEditValues((prev) => ({ ...prev, bait: value }))
            }
            renderInput={(params) => <TextField {...params} label="Bait" fullWidth />}
          />
          <Autocomplete
            freeSolo
            options={userRods.map((r) => r.name)}
            value={editValues.rod}
            onInputChange={(e, value) =>
              setEditValues((prev) => ({ ...prev, rod: value }))
            }
            renderInput={(params) => <TextField {...params} label="Rod" fullWidth />}
          />
          <TextField
            label="Location"
            fullWidth
            value={editValues.location}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const baseLat =
                        (editGeo && typeof editGeo.latitude === 'number' && editGeo.latitude) ||
                        (selectedCatch &&
                          typeof selectedCatch.latitude === 'number' &&
                          selectedCatch.latitude) ||
                        50.0;
                      const baseLon =
                        (editGeo && typeof editGeo.longitude === 'number' && editGeo.longitude) ||
                        (selectedCatch &&
                          typeof selectedCatch.longitude === 'number' &&
                          selectedCatch.longitude) ||
                        10.0;
                      setLocationFromCoords(baseLat, baseLon);
                      setLocationDialogOpen(true);
                    }}
                    aria-label="edit location on map"
                  >
                    <EditLocationAltIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setSelectedCatch(null);
              setEditImageFile(null);
              setEditImagePreview(null);
            }}
          >
            Cancel
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {selectedCatch && (
            <Button
              onClick={() => setDeleteDialogOpen(true)}
              color="error"
              sx={{ mr: 1 }}
            >
              Delete
            </Button>
          )}
          <Button variant="contained" onClick={handleSaveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location map dialog */}
      <Dialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Change location</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Tap on the map to choose the new catch location.
          </Typography>
          <Box
            sx={{
              mt: 2,
              mb: 2,
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.16)',
              overflow: 'hidden',
              height: 260,
            }}
          >
            <MapContainer
              center={[
                (editGeo && editGeo.latitude) ||
                  (selectedCatch && selectedCatch.latitude) ||
                  50.0,
                (editGeo && editGeo.longitude) ||
                  (selectedCatch && selectedCatch.longitude) ||
                  10.0,
              ]}
              zoom={8}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution="&copy; OpenStreetMap contributors &copy; CARTO"
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              />
              <LocationMarker
                position={
                  editGeo && typeof editGeo.latitude === 'number'
                    ? { lat: editGeo.latitude, lng: editGeo.longitude }
                    : selectedCatch &&
                        typeof selectedCatch.latitude === 'number' &&
                        typeof selectedCatch.longitude === 'number'
                      ? {
                          lat: selectedCatch.latitude,
                          lng: selectedCatch.longitude,
                        }
                      : null
                }
                onMove={(latlng) => {
                  if (!latlng) return;
                  setLocationFromCoords(latlng.lat, latlng.lng);
                }}
              />
            </MapContainer>
          </Box>
          <TextField
            label="Location"
            fullWidth
            value={editValues.location}
            InputProps={{ readOnly: true }}
            helperText={
              locationResolving
                ? 'Resolving place name...'
                : 'Location text is derived from the selected map position.'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setLocationDialogOpen(false);
            }}
          >
            Use this location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete catch</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this catch? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              handleDelete();
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
